import { useState } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import RecaptchaMessage from "./components/RecaptchaMessage";
import { convertToBase64String } from "../../common/Utility";
 
/**
 * Custom hook for handling Google reCAPTCHA verification.
 *
 * @param {Object} options - Configuration options for the reCAPTCHA.
 * @param {Function} options.recaptchaVerificationFn - Function to verify reCAPTCHA token with your backend.
 * @param {number} [options.tolerance=0.5] - Score threshold for successful verification.
 * @param {boolean} [options.enableLogging=false] - Flag to enable logging of the reCAPTCHA token.
 *
 * @returns {Object} - Returns an object containing:
 *   - {Function} handleCaptchaVerification: Function to initiate reCAPTCHA verification.
 *   - {Function} RecaptchaMessageComponent: React component to display verification messages.
 *   - {Object} recaptchaObject: State object with verification status, score, and message.
 */

const useRecaptcha = (options) => {

  const { recaptchaVerificationFn, tolerance=0.5,  enableLogging=false } = options;
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [recaptchaObject, setRecaptchaObject] = useState({
    success: undefined,
    score: undefined,
    message: "",
  });

  const handleCaptchaVerification = async () => {
    if (!recaptchaVerificationFn || typeof recaptchaVerificationFn !== "function") {
      console.error("Invalid RecaptchaVerification function provided.");
      return false;
    }

    if (!executeRecaptcha) {
      console.error("reCAPTCHA not ready.");
      return false;
    }

    try {
      const token = convertToBase64String(await executeRecaptcha("verify"));
      if(enableLogging) console.log("reCAPTCHA token:", token);
      const response = await recaptchaVerificationFn(token);

      const { score, success } = response.data; // Ensure response contains `data: { score, success }`
      let message = "";
      let isSuccess = success; // Create a new variable to track success state
      if (success) {
        if (score >= tolerance ) { // Example score tolerance
          message = "Verification completed successfully.";
        } else {
          isSuccess = false; // Update the new variable
          message = `Google Recaptcha Score too low: ${score}`;
        }
      } else {
        message = "Verification failed.";
      }
      
      setRecaptchaObject({
        score,
        success: isSuccess, // Use the updated variable
        message,
      });

      return isSuccess;
    } catch (error) {
      console.error("Error verifying reCAPTCHA:", error);
      setRecaptchaObject({
        score: undefined,
        success: false,
        message: "Unexpected error occurred during reCAPTCHA verification.",
      });
      return false;
    }
  };

  const RecaptchaComponent = () => (
    <RecaptchaMessage
      score={recaptchaObject.score}
      message={recaptchaObject.message}
      isSuccess={recaptchaObject.success}
    />
  );

  return { handleCaptchaVerification, RecaptchaComponent, recaptchaObject };
};

export default useRecaptcha;